import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnInit,
  ViewChild,
} from '@angular/core';
import { arrayIsShallowEqual, TimeTrackingPunch } from '@freelancer/datastore';
import { ButtonColor, ButtonSize } from '@freelancer/ui/button';
import { HeadingSize, HeadingType } from '@freelancer/ui/heading';
import { IconColor } from '@freelancer/ui/icon';
import { ImageSliderComponent, SliderImage } from '@freelancer/ui/image-slider';
import { Margin } from '@freelancer/ui/margin';
import { PictureObjectFit } from '@freelancer/ui/picture';
import { FontColor, FontSize, FontWeight } from '@freelancer/ui/text';
import * as Rx from 'rxjs';
import { distinctUntilChanged, map, take } from 'rxjs/operators';

@Component({
  template: `
    <ng-container *ngIf="images$ | async as images; else Loading">
      <fl-bit class="TitleRow">
        <fl-heading
          [headingType]="HeadingType.H1"
          [size]="HeadingSize.XXSMALL"
          [sizeTablet]="HeadingSize.SMALL"
          [flMarginRight]="Margin.XSMALL"
        >
          {{ images[currentImageIndex].timeCreated | date: 'mediumDate' }}
        </fl-heading>
        <fl-text [size]="FontSize.LARGE">
          {{ images[currentImageIndex].timeCreated | date: 'hh:mm a' }}
        </fl-text>
      </fl-bit>

      <!-- Main image preview -->
      <fl-image-slider
        class="ImageSlider"
        [images]="images"
        [externalSrc]="true"
        [objectFit]="PictureObjectFit.CONTAIN"
        (click)="handleImageSliderClick()"
      ></fl-image-slider>

      <fl-bit class="FooterRow">
        <fl-thumbnail-viewer
          class="ThumbnailViewer"
          [images]="images"
          [slidesToShow]="MAX_SLIDES_TO_SHOW_DESKTOP"
          [currentIndex]="currentImageIndex"
          [flHideMobile]="true"
          (slideChange)="handleSlideChange($event)"
        >
        </fl-thumbnail-viewer>
        <fl-thumbnail-viewer
          class="ThumbnailViewer"
          [images]="images"
          [slidesToShow]="MAX_SLIDES_TO_SHOW_TABLET"
          [currentIndex]="currentImageIndex"
          [flShowMobile]="true"
          (slideChange)="handleSlideChange($event)"
        >
        </fl-thumbnail-viewer>
        <fl-button
          [flHideMobile]="true"
          flTrackingLabel="DownloadScreenshotButton"
          i18n="Download time-tracking screenshot button"
          [color]="ButtonColor.DEFAULT"
          [size]="ButtonSize.SMALL"
          (click)="downloadImage()"
        >
          Download Image
        </fl-button>
      </fl-bit>
    </ng-container>

    <ng-template #Loading>
      <fl-bit class="LoadingSpinner">
        <fl-spinner [overlay]="true"></fl-spinner>
      </fl-bit>
    </ng-template>
  `,
  styleUrls: ['./punch-viewer-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PunchViewerModalComponent implements OnInit {
  Margin = Margin;
  HeadingType = HeadingType;
  HeadingSize = HeadingSize;
  FontColor = FontColor;
  FontSize = FontSize;
  FontWeight = FontWeight;
  IconColor = IconColor;
  ButtonColor = ButtonColor;
  ButtonSize = ButtonSize;
  PictureObjectFit = PictureObjectFit;

  // number of images shown per window of fl-thumbnail-viewer
  readonly MAX_SLIDES_TO_SHOW_DESKTOP = 5;
  readonly MAX_SLIDES_TO_SHOW_TABLET = 7;

  images$: Rx.Observable<ReadonlyArray<SliderImage & { timeCreated: number }>>;
  currentImageIndex = 0;

  @Input() punches$: Rx.Observable<ReadonlyArray<TimeTrackingPunch>>;

  @ViewChild(ImageSliderComponent, { static: false })
  imageSlider: ImageSliderComponent;

  ngOnInit() {
    this.images$ = this.punches$.pipe(
      distinctUntilChanged((a, b) =>
        arrayIsShallowEqual(
          a.map(punch => punch.timeCreated),
          b.map(punch => punch.timeCreated),
        ),
      ),
      map(punches =>
        punches.map(punch => ({
          src: punch.url ? punch.url : '',
          alt: punch.note ? punch.note : '',
          timeCreated: punch.timeCreated,
        })),
      ),
    );
  }

  handleImageSliderClick() {
    this.currentImageIndex = this.imageSlider.currentIndex;
  }

  handleSlideChange(currentIndex: number) {
    this.currentImageIndex = currentIndex;
    // Keep the image-slider in sync
    if (this.imageSlider) {
      this.imageSlider.slideToIndex(currentIndex);
    }
  }

  downloadImage() {
    // TODO: need more investigations on how to download image
    // instead of open it in a new tab
    // Ticket: T83769
    this.images$
      .pipe(take(1))
      .toPromise()
      .then(images => window.open(images[this.imageSlider.currentIndex].src));
  }
}
